import styled from 'styled-components';
import { getButtonStyle } from 'tokens';
import type { UiColor } from 'tokens';

export type Size = 'small' | 'default';

export const Button = styled.button<{
	$size: Size;
	$fullWidth: boolean;
	$color: UiColor;
}>`
	appearance: none;
	-webkit-appearance: none;
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	border: none;
	font-family: inherit;
	color: ${(p) => getButtonStyle(p.theme, p.$color).color};
	background: ${(p) => getButtonStyle(p.theme, p.$color).background};
	box-shadow: ${(p) => getButtonStyle(p.theme, p.$color).boxShadow};
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	position: relative;
	overflow: hidden;
	white-space: nowrap;
	font-weight: 600;
	width: ${(p) => (p.$fullWidth ? '100%' : 'fit-content')};

	${(p) =>
		p.$size === 'small'
			? `
		padding: 8px 18px 8px 14px;
		font-size: 13px;
		border-radius: 6px;

		&:hover:not([disabled]) {
			transform: translateY(-1px);
		}
	`
			: `
		height: 44px;
		padding: 12px 18px 12px 20px;
		font-size: 15px;
		border-radius: 8px;

		&:hover:not([disabled]) {
			transform: translateY(-2px);
		}
	`}

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
		opacity: 0;
		transition: opacity 0.2s ease;
	}

	&:hover:not([disabled]) {
		box-shadow: ${(p) => getButtonStyle(p.theme, p.$color).hoverBoxShadow};
	}

	&:hover:not([disabled])::before {
		opacity: 1;
	}

	&:active:not([disabled]) {
		transform: translateY(-1px);
		box-shadow: ${(p) => getButtonStyle(p.theme, p.$color).activeBoxShadow};
	}

	&:disabled {
		opacity: 0.5;
		transform: none;
		box-shadow: none;
		background: ${(p) => p.theme.gray[400]};
	}
`;

export const SplitContainer = styled.div<{ $fullWidth: boolean; $color: UiColor }>`
	display: flex;
	min-width: 0;
	height: 44px;
	border-radius: 8px;
	box-shadow: ${(p) => getButtonStyle(p.theme, p.$color).boxShadow};
	position: relative;
	width: ${(p) => (p.$fullWidth ? '100%' : 'fit-content')};
`;

export const SplitMain = styled.button<{ $color: UiColor }>`
	appearance: none;
	-webkit-appearance: none;
	box-sizing: border-box;
	flex: 1;
	min-width: 0;
	background: ${(p) => getButtonStyle(p.theme, p.$color).splitMainBackground};
	color: ${(p) => getButtonStyle(p.theme, p.$color).color};
	border: none;
	padding: 12px 18px 12px 20px;
	font-weight: 600;
	font-size: 15px;
	transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	position: relative;
	overflow: hidden;
	white-space: nowrap;
	border-radius: 8px 0 0 8px;
	font-family: inherit;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
		opacity: 0;
		transition: opacity 0.2s ease;
	}

	&:hover:not([disabled]) {
		filter: brightness(1.1);
	}

	&:hover:not([disabled])::before {
		opacity: 1;
	}

	&:active:not([disabled]) {
		filter: brightness(0.98);
	}

	&:disabled {
		opacity: 0.5;
	}
`;

export const SplitDropdown = styled.button<{ $color: UiColor }>`
	width: 40px;
	background: ${(p) => getButtonStyle(p.theme, p.$color).splitDropdownBg};
	border: none;
	border-left: 1px solid rgba(255, 255, 255, 0.2);
	color: ${(p) => getButtonStyle(p.theme, p.$color).color};
	transition: background 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border-radius: 0 8px 8px 0;
	overflow: hidden;

	&:hover {
		background: ${(p) => getButtonStyle(p.theme, p.$color).splitDropdownHoverBg};
	}

	&:active {
		background: ${(p) => getButtonStyle(p.theme, p.$color).splitDropdownActiveBg};
	}
`;

import {
	DropdownMenu as SharedDropdownMenu,
	DropdownMenuItem as SharedDropdownMenuItem,
} from 'components/Sidebar/dropdownStyles';

export const DropdownMenu = styled(SharedDropdownMenu)`
	top: calc(100% + 4px);
`;

export const DropdownMenuItem = SharedDropdownMenuItem;
